import React, { useEffect, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import { ModalProvider } from "./ModalContext";
import { useUser } from "../../UserContext";
import { deleteUserCookie, setLogoutSignal } from "../../utils";
import { apiFetch } from "../../services/apiFetch";

const apiBaseURL = process.env.REACT_APP_BACKEND_API_URL;

const fetchCourseModules = async (userId) => {
  if (!userId) return [];

  try {
    const coursesRes = await apiFetch(`${apiBaseURL}/courses`);
    if (!coursesRes.ok) return [];

    const courses = await coursesRes.json();
    if (!Array.isArray(courses) || courses.length === 0) return [];

    const firstCourse = courses[0];
    const progressRes = await apiFetch(
      `${apiBaseURL}/users/${userId}/courses/${firstCourse.id}/progress`
    );

    if (!progressRes.ok) return [];

    const progress = await progressRes.json();
    return progress?.modules || [];
  } catch (error) {
    console.error("Error loading course progress:", error);
    return [];
  }
};

const isSubsectionCompleted = (sub) =>
  sub?.completed === true ||
  sub?.completed === 1 ||
  sub?.completed === "true" ||
  sub?.status === "completed";

const isModuleCompletedByOrder = (modules, order) => {
  if (!Array.isArray(modules)) return false;
  const moduleItem = modules.find((m) => m.order === order);
  if (!moduleItem || !moduleItem.subsections?.length) return false;

  return moduleItem.subsections.every(isSubsectionCompleted);
};

const resolveCurrentPage = (pathname, hasModule1Completed, hasModule4Completed) => {
  // During onboarding (Module 1 not completed), don't highlight any sidebar item
  if (pathname.startsWith("/learner/learning")) {
    return hasModule1Completed ? "learning" : null;
  }
  // My Journey - always highlight when on this route (lock is enforced by sidebar, not highlighting)
  if (pathname.startsWith("/learner/journey")) {
    return "journey";
  }
  if (pathname.startsWith("/learner/action-plan")) return "action";
  if (pathname.startsWith("/learner/reflection")) return "reflection";
  return "space";
};

const LearnerShell = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, setUser } = useUser();
  const userId = user?.user_id;
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [layoutPhase, setLayoutPhase] = useState(() =>
    location.pathname === "/learner" || location.pathname === "/learner/"
      ? "immersive"
      : "intro"
  );
  const [hasModule1Completed, setHasModule1Completed] = useState(false);
  const [hasModule4Completed, setHasModule4Completed] = useState(false);
  const [isCheckingAccess, setIsCheckingAccess] = useState(true);
  const [resetKey, setResetKey] = useState(0);
  
  // Recalculate currentPage whenever location or module completion states change
  const currentPage = React.useMemo(
    () => resolveCurrentPage(location.pathname, hasModule1Completed, hasModule4Completed),
    [location.pathname, hasModule1Completed, hasModule4Completed]
  );

  useEffect(() => {
    const { pathname } = location;
    if (
      pathname === "/learner" ||
      pathname === "/learner/" ||
      pathname.startsWith("/learner/learning")
    ) {
      setLayoutPhase("immersive");
      return;
    }
    if (pathname.startsWith("/learner")) {
      setLayoutPhase("intro");
    }
  }, [location]);

  useEffect(() => {
    let isMounted = true;

    const checkAccess = async () => {
      if (!userId) {
        if (isMounted) {
          setIsCheckingAccess(false);
          setHasModule1Completed(false);
        }
        return;
      }

      setIsCheckingAccess(true);
      const modules = await fetchCourseModules(userId);
      const module1Completed = isModuleCompletedByOrder(modules, 1);
      const module4Completed = isModuleCompletedByOrder(modules, 4);

      if (isMounted) {
        setHasModule1Completed(module1Completed);
        setHasModule4Completed(module4Completed);
        setIsCheckingAccess(false);
      }
    };

    checkAccess();

    return () => {
      isMounted = false;
    };
  }, [userId]);

  useEffect(() => {
    if (isCheckingAccess || !userId) return;

    const isLanding =
      location.pathname === "/learner" || location.pathname === "/learner/";

    if (isLanding && !hasModule1Completed) {
      navigate("/learner/learning", { replace: true });
    }
  }, [hasModule1Completed, isCheckingAccess, location.pathname, navigate, userId]);

  const isLanding =
    location.pathname === "/learner" || location.pathname === "/learner/";
  const shouldHoldLanding =
    isLanding && (isCheckingAccess || !hasModule1Completed);

  if (shouldHoldLanding) {
    return (
      <div className="w-full h-screen flex items-center justify-center bg-white">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-gray-900"></div>
          <p className="text-gray-500 text-sm font-medium">Loading...</p>
        </div>
      </div>
    );
  }

  const handleLogout = () => {
    setLogoutSignal();
    setUser({ user_id: "", firstName: "", lastName: "" });
    localStorage.removeItem("user");
    deleteUserCookie();
    window.location.href = "/";
  };

  const handleNavigate = (pageId) => {
    if (pageId === "logout") {
      handleLogout();
      return;
    }

    const routes = {
      learning: "/learner/learning",
      space: "/learner",
      journey: "/learner/journey",
      action: "/learner/action-plan",
      reflection: "/learner/reflection",
    };
    const target = routes[pageId];
    if (target) {
      // If navigating to learning path, increment reset key to force state reset
      if (pageId === "learning") {
        setResetKey(prev => prev + 1);
      }
      navigate(target);
    }
  };

  return (
    <ModalProvider>
      {layoutPhase !== "full" && (
        <Sidebar
          sidebarOpen={sidebarOpen}
          onOpen={() => setSidebarOpen(true)}
          onClose={() => setSidebarOpen(false)}
          currentPage={currentPage}
          onNavigate={handleNavigate}
        />
      )}
      <Outlet context={{ layoutPhase, setLayoutPhase, resetKey }} />
    </ModalProvider>
  );
};

export default LearnerShell;
